/**
 * @typedef ProcessedResponse
 * @type {object}
 * @property {boolean} ok - successful status of request
 * @property {object} headers - object with response headers.
 * @property {number} status - status of request.
 * @property {Response} response - response itself.
 * @property {function} text - method for getting text view of response.
 * @property {function} json - method for getting json view of response.
 */

/**
 * resolve response handler
 * @param {Response} response - response of success request
 * @returns {ProcessedResponse}
 */
export const fetchSuccessHandler = response => {
    const res = response.response ? response.response() : response;

    return {
        ok: res.ok,
        headers: res.headers,
        status: res.status,
        response: res,
        text: () => Promise.resolve(response.text()),
        json: () => Promise.resolve(response.json()),
    };
};

/**
 * @typedef RejectedResponse
 * @type {object}
 * @property {APIResponse|unknown} apiResponse - response data
 */

/**
 * @typedef RejectedProcessedResponse
 * @type {object}
 * @property {function} text - method for getting text view of response.
 * @property {function} json - method for getting json view of response.
 * @property {number} status - status of request.
 */

/**
 * rejected response handler
 * @param {RejectedResponse} error - response of failed request
 * @returns {RejectedProcessedResponse}
 */

/**
 * @typedef APIResponse - SDK3 response
 * @type {object}
 * @property {function} response - method for getting response
 * @property {string} _text - text view of response.
 * @property {object} _json - json view of response.
 * @property {function} text - method for getting text view of response.
 * @property {function} json - method for getting json view of response.
 * @property {number} response().status - status of request.
 */

const handleSDK3Error = error => {
    const apiResponse = error.apiResponse;
    const response = typeof apiResponse?.response === 'function' ? apiResponse.response() : null;

    return (
        response && {
            ok: response.ok,
            headers: response.headers,
            status: response.status,
            response,
            text: () => Promise.resolve(apiResponse._text || {}),
            json: () => Promise.resolve(apiResponse._json || {}),
        }
    );
};

/**
 * @typedef error SDK4 error
 * @type {object}
 * @property {string} message - error message
 * @property {string|unknown} originalMessage - error message
 * @property {object|null} response - response from fetch API or null with reject request.
 * @property {object} request - request from fetch API.
 */

const handleSDK4Error = error => {
    const response = error.response;

    return (
        response && {
            ok: response.ok,
            headers: response.headers,
            status: response.status,
            response,
            text: () => response.text(),
            json: () => response.json(),
        }
    );
};

export const createPlatformSDKFetchFailureHandler = statusText => error => {
    let response = null;

    if (error?.response) {
        response = handleSDK4Error(error);
    } else if (error?.apiResponse) {
        response = handleSDK3Error(error);
    }

    const errorText = error?.toString() || `Reason: ${statusText}`;

    return (
        response ??
        new Response(errorText, {
            // Client error status code introduced for unified error handling with backend
            // check full error message before reporting an issue
            // if there is a "TypeError: Failed to fetch" log, it's a network/cors issue
            // and reason is opaque for the js code
            status: 557,
            statusText: `SDK fetch reject: ${statusText}`,
        })
    );
};
